import api, { API_BASE_URL } from '@config/api';
import type {
  Share,
  CreateShareDto,
  UpdateShareDto,
  ShareResourceType,
  EffectiveAccess,
  ShareAccessResponse,
  ShareDownloadResponse,
  SharedFolderResource,
  ShareStreamResponse,
} from '../types';

const SHARE_PASSWORD_HEADER = 'x-share-password';

const passwordHeaders = (password?: string) =>
  password ? { [SHARE_PASSWORD_HEADER]: password } : undefined;

export const shareService = {
  // ─── Resource shares (autenticado) ───────────────────────────────────

  listForResource: async (
    resourceType: ShareResourceType,
    resourceId: string,
  ): Promise<Share[]> => {
    const response = await api.get('/shares', {
      params: { resourceType, resourceId },
    });
    return response.data;
  },

  checkAccess: async (
    resourceType: ShareResourceType,
    resourceId: string,
  ): Promise<EffectiveAccess> => {
    const response = await api.get('/shares/check', {
      params: { resourceType, resourceId },
    });
    return response.data;
  },

  create: async (data: CreateShareDto): Promise<Share> => {
    const response = await api.post('/shares', data);
    return response.data;
  },

  update: async (id: string, data: UpdateShareDto): Promise<Share> => {
    const response = await api.patch(`/shares/${id}`, data);
    return response.data;
  },

  rotateToken: async (id: string): Promise<Share> => {
    const response = await api.post(`/shares/${id}/rotate-token`);
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/shares/${id}`);
  },

  // ─── Public access via token ──────────────────────────────────────────

  accessShare: async (
    token: string,
    password?: string,
  ): Promise<ShareAccessResponse> => {
    const response = await api.get(`/shares/public/${token}`, {
      headers: passwordHeaders(password),
    });
    return response.data;
  },

  accessFolderInShare: async (
    token: string,
    folderId: string,
    password?: string,
  ): Promise<SharedFolderResource> => {
    const response = await api.get(
      `/shares/public/${token}/folder/${folderId}`,
      { headers: passwordHeaders(password) },
    );
    return response.data;
  },

  downloadShare: async (
    token: string,
    password?: string,
  ): Promise<ShareDownloadResponse> => {
    const response = await api.get(`/shares/public/${token}/download`, {
      headers: passwordHeaders(password),
    });
    return response.data;
  },

  downloadFileInShare: async (
    token: string,
    fileId: string,
    password?: string,
  ): Promise<ShareDownloadResponse> => {
    const response = await api.get(
      `/shares/public/${token}/file/${fileId}/download`,
      { headers: passwordHeaders(password) },
    );
    return response.data;
  },

  streamFileInShare: async (
    token: string,
    fileId: string,
    password?: string,
  ): Promise<ShareStreamResponse> => {
    const response = await api.get(
      `/shares/public/${token}/file/${fileId}/stream`,
      { headers: passwordHeaders(password) },
    );
    return response.data;
  },

  /**
   * URL absoluta para download da pasta compartilhada (raiz do share) como
   * ZIP streamed. Quando há senha, ela DEVE ser enviada via header — esta URL
   * não aceita query string para senha por questões de log/cache. Use
   * `downloadFolderShareViaBlob` quando o share for protegido por senha.
   */
  getFolderShareDownloadUrl: (token: string, folderId?: string): string => {
    return folderId
      ? `${API_BASE_URL}/shares/public/${token}/folder/${encodeURIComponent(folderId)}/download`
      : `${API_BASE_URL}/shares/public/${token}/folder-download`;
  },

  /**
   * Download via blob quando a senha precisa viajar no header. Bufferiza o
   * ZIP em memória — preferir `getFolderShareDownloadUrl` quando não houver
   * senha (streaming direto ao disco).
   */
  downloadFolderShareViaBlob: async (
    token: string,
    folderId: string | undefined,
    password: string | undefined,
    suggestedFileName: string,
  ): Promise<void> => {
    const url = folderId
      ? `/shares/public/${token}/folder/${encodeURIComponent(folderId)}/download`
      : `/shares/public/${token}/folder-download`;
    const response = await api.get(url, {
      responseType: 'blob',
      headers: passwordHeaders(password),
    });
    const blob = response.data as Blob;
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = suggestedFileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
  },
};
